/**
 * Keeps the PDP free-shipping USP in step with the cart.
 *
 * Liquid renders the first message (blocks/free-shipping-usp.liquid); this re-resolves it from the
 * GM_STATE tiers whenever the cart changes. It reads the cart the Cart helper has already fetched
 * rather than calling getCart(), which itself dispatches the cart-count event and would loop.
 */

interface FreeShippingTier {
  minThreshold: number
  message: string
  thresholdMessage: string
  countries: string[]
}

declare global {
  interface Window {
    GM_STATE?: {
      metaobjects?: { free_shipping_usp?: FreeShippingTier[] }
      localization?: { country?: string }
    }
    Cart?: { value?: { total_price?: number } }
  }
}

const CART_COUNT_EVENT = 'updateCartCount'
const MESSAGE_SELECTOR = '[data-role="free-shipping-usp-message"]'

const getTiers = (): FreeShippingTier[] => {
  const tiers = window.GM_STATE?.metaobjects?.free_shipping_usp
  if (!Array.isArray(tiers)) return []

  const country = window.GM_STATE?.localization?.country
  return tiers
    .filter((tier) => !country || !tier.countries?.length || tier.countries.includes(country))
    .sort((a, b) => a.minThreshold - b.minThreshold)
}

/** Mirrors the Liquid resolution so the server and client never disagree on a given cart total. */
export const resolveUspMessage = (tiers: FreeShippingTier[], cartTotal: number, productPriceMin: number): string => {
  if (!tiers.length) return ''

  const thresholdTotal = cartTotal + productPriceMin
  const lowest = tiers[0]
  let current: FreeShippingTier | null = null
  let next: FreeShippingTier | null = null
  let product: FreeShippingTier | null = null
  for (const tier of tiers) {
    if (cartTotal >= tier.minThreshold) current = tier
    if (thresholdTotal >= tier.minThreshold) next = tier
    if (productPriceMin >= tier.minThreshold) product = tier
  }

  // A product that clears a real threshold on its own qualifies regardless of the cart.
  if (product && product.minThreshold > lowest.minThreshold) return product.message
  if (!current) {
    if (!next) return ''
    return lowest.thresholdMessage || next.message
  }
  if (next && next.minThreshold !== current.minThreshold) {
    return current.thresholdMessage || next.message
  }
  return current.message
}

class FreeShippingUSP extends HTMLElement {
  private lastMessage = ''

  private handleCartUpdate = () => {
    const cart = window.Cart?.value
    if (!cart) return

    const priceMin = Number(this.dataset.productPriceMin) || 0
    const message = resolveUspMessage(getTiers(), Number(cart.total_price) || 0, priceMin)
    if (!message || message === this.lastMessage) return

    const target = this.querySelector(MESSAGE_SELECTOR)
    if (target) target.innerHTML = message
    this.lastMessage = message
  }

  connectedCallback() {
    document.addEventListener(CART_COUNT_EVENT, this.handleCartUpdate)
    this.handleCartUpdate()
  }

  disconnectedCallback() {
    document.removeEventListener(CART_COUNT_EVENT, this.handleCartUpdate)
  }
}

if (!customElements.get('free-shipping-usp')) {
  customElements.define('free-shipping-usp', FreeShippingUSP)
}
